/**
 * Semver Helper
 * Lightweight semantic versioning utilities without external dependencies
 * Supports format: major.minor.patch (e.g., "1.2.3")
 */

/**
 * Parsed version components
 */
export interface ParsedVersion {
  major: number;
  minor: number;
  patch: number;
  raw: string;
}

/**
 * Version constraint options
 */
export interface VersionConstraint {
  /** Minimum version (inclusive) */
  min?: string;
  /** Maximum version (inclusive) */
  max?: string;
  /** Exact version match */
  exact?: string;
}

/**
 * Parse a version string into its components
 * @param version Version string (e.g., "1.2.3", "1.0", "2")
 * @returns Parsed version or null if invalid
 */
export function parseVersion(version: string): ParsedVersion | null {
  if (!version || typeof version !== 'string') {
    return null;
  }

  // Remove 'v' prefix if present
  const cleaned = version.trim().replace(/^v/i, '');
  
  // Match major.minor.patch pattern (minor and patch optional)
  const match = cleaned.match(/^(\d+)(?:\.(\d+))?(?:\.(\d+))?/);
  
  if (!match) {
    return null;
  }

  return {
    major: parseInt(match[1], 10) || 0,
    minor: parseInt(match[2], 10) || 0,
    patch: parseInt(match[3], 10) || 0,
    raw: version,
  };
}

/**
 * Compare two version strings
 * @param v1 First version
 * @param v2 Second version
 * @returns -1 if v1 < v2, 0 if equal, 1 if v1 > v2
 */
export function compareVersions(v1: string, v2: string): -1 | 0 | 1 {
  const parsed1 = parseVersion(v1);
  const parsed2 = parseVersion(v2);

  // If either is invalid, treat as equal (fail safely)
  if (!parsed1 || !parsed2) {
    return 0;
  }

  // Compare major
  if (parsed1.major > parsed2.major) return 1;
  if (parsed1.major < parsed2.major) return -1;

  // Compare minor
  if (parsed1.minor > parsed2.minor) return 1;
  if (parsed1.minor < parsed2.minor) return -1;

  // Compare patch
  if (parsed1.patch > parsed2.patch) return 1;
  if (parsed1.patch < parsed2.patch) return -1;

  return 0;
}

/**
 * Check if a version is greater than or equal to another
 */
export function isVersionGte(version: string, min: string): boolean {
  return compareVersions(version, min) >= 0;
}

/**
 * Check if a version is less than or equal to another
 */
export function isVersionLte(version: string, max: string): boolean {
  return compareVersions(version, max) <= 0;
}

/**
 * Check if a version equals another
 */
export function isVersionEqual(version: string, target: string): boolean {
  return compareVersions(version, target) === 0;
}

/**
 * Check if a version satisfies the given constraints
 * @param version The version to check
 * @param constraint Version constraints (min, max, exact)
 * @returns true if version satisfies all constraints
 */
export function satisfiesVersion(version: string | null | undefined, constraint: VersionConstraint): boolean {
  // If no version provided, constraints are not satisfied
  if (!version) {
    return false;
  }

  const { min, max, exact } = constraint;

  // If no constraints, always satisfied
  if (!min && !max && !exact) {
    return true;
  }

  // Exact match takes precedence
  if (exact) {
    return isVersionEqual(version, exact);
  }

  // Check min constraint
  if (min && !isVersionGte(version, min)) {
    return false;
  }

  // Check max constraint
  if (max && !isVersionLte(version, max)) {
    return false;
  }

  return true;
}

/**
 * Format a version for display
 */
export function formatVersion(version: string | null | undefined): string {
  if (!version) return 'Unknown';
  const parsed = parseVersion(version);
  if (!parsed) return version;
  return `${parsed.major}.${parsed.minor}.${parsed.patch}`;
}
